import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import type { StaticDatabase } from "@-/dev.improve.haniverse.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { ensureJiebaReady } from "@-/com.npmjs.react-native-jieba";
import { eq, inArray } from "@-/com.npmjs.drizzle-orm";
import runes from "@-/com.npmjs.runes2";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { hasHanScript } from "@-/dev.improve.haniverse.lib.unicode";

import { ChineseWordsTrie } from "#chinese-words-trie";
import { getSentenceWords } from "#get-sentence-words";
import { hasLength, hasMinLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { useCallback } from "react";

/**
 * Longest multi-character word the compiled vocabulary contains. The compiled `word` table only ever
 * holds words of length ≥ 2 (single graphemes are never word rows), so candidate substrings start at
 * length 2 and stop here — anything longer can't be a row, so generating it would just bloat the
 * `IN` list.
 */
const MAX_WORD_LENGTH = 19;
const MIN_WORD_LENGTH = 2;

/**
 * Every multi-character substring of `sentence`'s Han runs that could be a word row. A sentence is
 * short, so this is a small set (bounded by run length × {@link MAX_WORD_LENGTH}) — far below SQLite's
 * variable limit. Non-Han characters break runs, so e.g. punctuation never anchors a candidate.
 */
function candidateWords(sentence: string): string[] {
  const candidates = new Set<string>();
  const chars = runes(sentence);
  for (let start = 0; start < arrayLength(chars); start += 1) {
    if (!hasHanScript(chars[start] ?? "")) {
      continue;
    }
    // Extend the run while the characters stay Han, capping at the longest possible word.
    let end = start + MIN_WORD_LENGTH;
    while (hasMinLength(chars, end) && end - start <= MAX_WORD_LENGTH) {
      const slice = chars.slice(start, end);
      if (!hasHanScript(slice.at(-1) ?? "")) {
        break;
      }
      candidates.add(slice.join(""));
      end += 1;
    }
  }
  return [...candidates];
}

/**
 * Segment `sentence` into tappable words using only the vocabulary the sentence could possibly
 * contain. Instead of bundling the full 256k-word list and building a trie over all of it, we query
 * the static `word` table for the handful of forms that appear as substrings of this sentence, build
 * a small trie from those, and run the same jieba + longest-match segmentation.
 */
export function useGetSentenceWordsQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  return useCallback(
    (sentence: string) => ["sentence-words", sentence, { staticDb }] as const,
    [staticDb],
  );
}

/**
 * Look up the reading (tone-numbered pinyin) for each distinct segmented word. Multi-character words
 * read from `word_definition`, single graphemes from `grapheme_definition` — in both cases the first
 * definition (lowest `index`) supplies the reading. Non-Han segments (punctuation, latin) and words
 * with no dictionary row map to `null`. Returns a `word → pinyin` map keyed by the exact segment text.
 */
async function readingsForSegments(
  staticDb: StaticDatabase,
  segments: readonly string[],
): Promise<Map<string, string>> {
  const readings = new Map<string, string>();

  const hanSegments = [...new Set(segments.filter((segment) => hasHanScript(segment)))];
  if (hasLength(hanSegments, 0)) {
    return readings;
  }

  const multiCharWords = hanSegments.filter((segment) => hasMinLength(runes(segment), 2));
  const singleChars = hanSegments.filter((segment) => hasLength(runes(segment), 1));

  if (isNonEmpty(multiCharWords)) {
    // Join `word` → `word_definition` and keep the first definition's pinyin per word form. A word
    // form can repeat across word ids (homographs); the lowest-`index` definition of any of them is
    // a good enough reading for a reading aid, so we take the first row seen after ordering by index.
    const wordRows = await staticDb(import.meta.location)
      .select({
        index: staticSchema.wordDefinition.index,
        pinyin: staticSchema.wordDefinition.pinyin,
        word: staticSchema.word.word,
      })
      .from(staticSchema.word)
      .innerJoin(
        staticSchema.wordDefinition,
        eq(staticSchema.wordDefinition.wordId, staticSchema.word.id),
      )
      .where(inArray(staticSchema.word.word, multiCharWords))
      .orderBy(staticSchema.wordDefinition.index);

    for (const row of wordRows) {
      if (!readings.has(row.word)) {
        readings.set(row.word, row.pinyin);
      }
    }
  }

  const missingSingleChars = singleChars.filter((segment) => !readings.has(segment));
  if (isNonEmpty(missingSingleChars)) {
    // Single characters aren't word rows, so read them from `grapheme_definition` via `grapheme`.
    const graphemeRows = await staticDb(import.meta.location)
      .select({
        glyph: staticSchema.grapheme.glyph,
        index: staticSchema.graphemeDefinition.index,
        pinyin: staticSchema.graphemeDefinition.pinyin,
      })
      .from(staticSchema.grapheme)
      .innerJoin(
        staticSchema.graphemeDefinition,
        eq(staticSchema.graphemeDefinition.graphemeId, staticSchema.grapheme.id),
      )
      .where(inArray(staticSchema.grapheme.glyph, missingSingleChars))
      .orderBy(staticSchema.graphemeDefinition.index);

    for (const row of graphemeRows) {
      if (!readings.has(row.glyph)) {
        readings.set(row.glyph, row.pinyin);
      }
    }
  }

  return readings;
}

export const [useSentenceWordsQueryOptions, sentenceWordsQueryFn] = createUseQueryOptions(
  useGetSentenceWordsQueryKey,
  async ({ queryKey: [_, sentence, { staticDb }] }) => {
    // Non-sentence drills use an empty sentinel to preserve hook order. It must remain inert:
    // preparing Jieba here otherwise builds its native dictionary for every character review.
    if (sentence.length === 0) {
      return [];
    }
    // Load jieba's wasm on demand. It is no longer warmed at import, so the binary is only
    // fetched the first time a sentence is actually segmented (see react-native-jieba.web.ts).
    await ensureJiebaReady();

    const candidates = candidateWords(sentence);
    if (hasLength(candidates, 0)) {
      // No Han: jieba/longest-match never consults the vocabulary, so an empty trie is fine, and
      // there are no readings to attach.
      return getSentenceWords(sentence, new ChineseWordsTrie([])).map((word) => ({
        pinyin: null,
        word,
      }));
    }

    const rows = await staticDb(import.meta.location)
      .select({ word: staticSchema.word.word })
      .from(staticSchema.word)
      .where(inArray(staticSchema.word.word, candidates));

    const knownWords = new ChineseWordsTrie(rows.map((r) => r.word));
    const segments = getSentenceWords(sentence, knownWords);

    const readings = await readingsForSegments(staticDb, segments);
    return segments.map((word) => ({ pinyin: readings.get(word) ?? null, word }));
  },
);
